import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import {
  Sheet, SheetContent, SheetHeader, SheetTitle,
} from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@/components/ui/select";
import {
} from "@/components/ui/accordion";
import { Plus, Trash2, MessageSquare, Send, Loader2, Calendar, Copy } from "lucide-react";
import { PRIORITY, initials, timeAgo, fmtDate, AgingBadge, labelColor } from "@/lib/ui";
import { toast } from "sonner";
import { AssigneePicker, assigneeIds } from "@/components/Assignees";
import { UserPlus } from "lucide-react";

function SubtaskDue({ sub, max, onChange }) {
  const overdue = sub.due_date && !sub.done && sub.due_date < new Date().toLocaleDateString("en-CA");
  return (
    <label className={`relative h-7 px-1.5 rounded-md border flex items-center gap-1 text-[11px] cursor-pointer shrink-0 ${overdue ? "border-red-200 bg-red-50 text-red-600 font-medium" : sub.due_date ? "border-slate-200 text-slate-600" : "border-transparent text-slate-400 hover:border-slate-200"}`}
      title={max ? `Must be on or before ${max}` : "Due date"} data-testid={`subtask-due-${sub.id}`}>
      <Calendar className="w-3 h-3" />{sub.due_date ? fmtDate(sub.due_date + "T00:00:00") : <span className="hidden sm:inline">Due</span>}
      <input type="date" value={sub.due_date || ""} max={max} onChange={(e) => onChange(e.target.value)}
        className="absolute inset-0 opacity-0 cursor-pointer" data-testid={`subtask-due-input-${sub.id}`} />
    </label>
  );
}

function SubtaskAssignee({ sub, staff, onChange }) {
  const u = staff.find((x) => x.id === sub.assignee_id);
  return (
    <Select value={sub.assignee_id || "none"} onValueChange={(v) => onChange(v === "none" ? null : v)}>
      <SelectTrigger className="h-7 w-auto max-w-[140px] gap-1 px-1.5 border-transparent hover:border-slate-200 text-xs whitespace-nowrap [&>span]:flex [&>span]:items-center [&>svg]:w-3 [&>svg]:h-3" data-testid={`subtask-assignee-select-${sub.id}`} title={u ? u.name : "Assign"}>
        {u ? (
          <span className="flex items-center gap-1.5 min-w-0">
            <Avatar className="w-5 h-5"><AvatarImage src={u.avatar} /><AvatarFallback className="bg-indigo-100 text-indigo-700 text-[8px]">{initials(u.name)}</AvatarFallback></Avatar>
            <span className="truncate text-slate-600 hidden sm:inline">{u.name.split(" ")[0]}</span>
          </span>
        ) : <span className="flex items-center gap-1 text-slate-400"><UserPlus className="w-3.5 h-3.5" /><span className="hidden sm:inline">Assign</span></span>}
      </SelectTrigger>
      <SelectContent>
        <SelectItem value="none">Unassigned</SelectItem>
        {staff.map((x) => <SelectItem key={x.id} value={x.id}>{x.name}</SelectItem>)}
      </SelectContent>
    </Select>
  );
}

function NoteList({ notes }) {
  if (!notes?.length) return <p className="text-xs text-slate-400 py-2">No notes yet.</p>;
  return (
    <div className="space-y-3 mt-2">
      {notes.map((n) => (
        <div key={n.id} className="flex items-start gap-2.5" data-testid={`note-${n.id}`}>
          <Avatar className="w-6 h-6 mt-0.5"><AvatarImage src={n.author_avatar} /><AvatarFallback className="bg-indigo-100 text-indigo-700 text-[9px]">{initials(n.author_name)}</AvatarFallback></Avatar>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-1.5 text-xs">
              <span className="font-semibold text-slate-700">{n.author_name}</span>
              <span className="text-slate-400">· {timeAgo(n.created_at)}</span>
            </div>
            <p className="text-sm text-slate-600 mt-0.5 whitespace-pre-wrap">{n.content}</p>
          </div>
        </div>
      ))}
    </div>
  );
}

function SubtaskNotes({ subtaskId, onAdded }) {
  const [notes, setNotes] = useState([]);
  const [val, setVal] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    api.get(`/subtasks/${subtaskId}/notes`).then((r) => setNotes(r.data)).catch(() => setNotes([]));
  }, [subtaskId]);

  const add = async () => {
    if (!val.trim()) return;
    setLoading(true);
    try {
      const { data } = await api.post("/notes", { target_type: "subtask", target_id: subtaskId, content: val });
      setNotes((p) => [data, ...p]);
      setVal("");
      onAdded?.();
      toast.success("Note added");
    } catch { toast.error("Could not add note"); }
    finally { setLoading(false); }
  };

  return (
    <div className="pt-1">
      <div className="flex gap-2">
        <Input value={val} onChange={(e) => setVal(e.target.value)} onKeyDown={(e) => e.key === "Enter" && add()}
          placeholder="Add a note…" className="h-8 text-sm" data-testid={`subtask-note-textarea-${subtaskId}`} />
        <Button size="sm" className="h-8 bg-indigo-600 hover:bg-indigo-700" onClick={add} disabled={loading}
          data-testid={`subtask-add-note-button-${subtaskId}`}>
          {loading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />}
        </Button>
      </div>
      <NoteList notes={notes} />
    </div>
  );
}

export default function TaskDrawer({ taskId, users, onClose, onChanged }) {
  const [task, setTask] = useState(null);
  const [boards, setBoards] = useState([]);
  const [note, setNote] = useState("");
  const [newSub, setNewSub] = useState("");
  const [savingNote, setSavingNote] = useState(false);
  const [openNotes, setOpenNotes] = useState({});
  const toggleNotes = (id) => setOpenNotes((p) => ({ ...p, [id]: !p[id] }));
  const userList = Object.values(users);
  const staff = userList.filter((u) => u.role !== "client");
  const clients = userList.filter((u) => u.role === "client");

  const load = async () => {
    const { data } = await api.get(`/tasks/${taskId}`);
    setTask(data);
  };
  useEffect(() => { load(); }, [taskId]);
  useEffect(() => { api.get("/boards").then((r) => setBoards(r.data)).catch(() => {}); }, []);

  const moveToBoard = async (boardId) => {
    if (boardId === task.board_id) return;
    const { data } = await api.get(`/boards/${boardId}`);
    const first = data.stages[0];
    if (!first) { toast.error("That board has no stages yet"); return; }
    await api.patch(`/tasks/${taskId}`, { board_id: boardId, stage_id: first.id });
    await load();
    onChanged?.();
    toast.success(`Moved to ${data.board.name}`);
  };

  const duplicate = async () => {
    await api.post(`/tasks/${taskId}/duplicate`);
    toast.success("Task duplicated — edit the copy");
    onChanged?.();
    onClose();
  };

  const patch = async (updates) => {
    await api.patch(`/tasks/${taskId}`, updates);
    await load();
    onChanged?.();
  };

  const addNote = async () => {
    if (!note.trim()) return;
    setSavingNote(true);
    try {
      await api.post("/notes", { target_type: "task", target_id: taskId, content: note });
      setNote("");
      await load();
      onChanged?.();
      toast.success("Note added");
    } catch { toast.error("Could not add note"); }
    finally { setSavingNote(false); }
  };

  const addSubtask = async () => {
    if (!newSub.trim()) return;
    await api.post(`/tasks/${taskId}/subtasks`, { title: newSub });
    setNewSub("");
    await load();
    onChanged?.();
    toast.success("Subtask added");
  };

  const toggleSub = async (sub) => {
    await api.patch(`/subtasks/${sub.id}`, { done: !sub.done });
    await load();
    onChanged?.();
  };

  const setSubAssignee = async (sub, v) => {
    await api.patch(`/subtasks/${sub.id}`, { assignee_id: v });
    await load();
    onChanged?.();
    toast.success(v ? `Subtask assigned to ${staff.find((u) => u.id === v)?.name}` : "Subtask unassigned");
  };

  const setSubDue = async (sub, v) => {
    try {
      await api.patch(`/subtasks/${sub.id}`, { due_date: v || null });
      await load();
      onChanged?.();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not set due date"); await load(); }
  };

  const deleteSub = async (id) => {
    await api.delete(`/subtasks/${id}`);
    await load();
    onChanged?.();
  };

  const deleteTask = async () => {
    if (!window.confirm("Delete this task?")) return;
    await api.delete(`/tasks/${taskId}`);
    toast.success("Task deleted");
    onChanged?.();
    onClose();
  };

  return (
    <Sheet open onOpenChange={(o) => !o && onClose()}>
      <SheetContent className="w-full sm:max-w-[560px] overflow-y-auto scroll-thin p-0" data-testid="task-detail-drawer">
        {!task ? (
          <div className="p-8 text-slate-400 flex items-center gap-2"><SheetTitle className="sr-only">Loading task</SheetTitle><Loader2 className="w-4 h-4 animate-spin" />Loading…</div>
        ) : (
          <div>
            <SheetHeader className="p-6 pb-4 border-b border-slate-100">
              <div className="flex items-center gap-2 mb-2">
                <Badge variant="outline" className="h-5 px-1.5 text-[10px] border-slate-200 text-slate-500">{task.board_name}</Badge>
                <Badge variant="outline" className="h-5 px-1.5 text-[10px] border-slate-200 text-slate-500">{task.stage_name}</Badge>
                <AgingBadge age={task.age_days} inStage={task.days_in_stage} />
              </div>
              <SheetTitle className="text-left pr-8">
                <input key={task.id} defaultValue={task.title} data-testid="task-title-input"
                  onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); e.target.blur(); } }}
                  onBlur={(e) => { const v = e.target.value.trim(); if (v && v !== task.title) patch({ title: v }); }}
                  className="w-full font-display text-xl font-bold text-slate-900 bg-transparent outline-none focus:bg-slate-50 rounded px-1 -mx-1 border border-transparent focus:border-indigo-200" />
              </SheetTitle>
            </SheetHeader>

            <div className="p-6 space-y-6">
              {/* Meta controls */}
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-1.5">
                  <label className="text-xs font-semibold uppercase tracking-wider text-slate-400">Status</label>
                  <Select value={task.stage_id} onValueChange={(v) => patch({ stage_id: v })}>
                    <SelectTrigger className="h-9" data-testid="task-stage-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {(task._stages || []).length === 0 && null}
                      <StageOptions boardId={task.board_id} />
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1.5">
                  <label className="text-xs font-semibold uppercase tracking-wider text-slate-400">Priority</label>
                  <Select value={task.priority} onValueChange={(v) => patch({ priority: v })}>
                    <SelectTrigger className="h-9" data-testid="task-priority-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {Object.entries(PRIORITY).map(([k, v]) => <SelectItem key={k} value={k}>{v.label}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1.5">
                  <label className="text-xs font-semibold uppercase tracking-wider text-slate-400">Assignees</label>
                  <AssigneePicker value={assigneeIds(task)} onChange={(ids) => patch({ assignee_ids: ids })} staff={staff} testid="task-assignee-select" />
                </div>
                <div className="space-y-1.5">
                  <label className="text-xs font-semibold uppercase tracking-wider text-slate-400">Due date</label>
                  <Input type="date" className="h-9" data-testid="task-due-date-input"
                    value={task.due_date ? task.due_date.slice(0, 10) : ""}
                    onChange={async (e) => { try { await patch({ due_date: e.target.value ? new Date(e.target.value).toISOString() : null }); } catch (err) { toast.error(err.response?.data?.detail || "Could not change due date"); await load(); } }} />
                </div>
                <div className="space-y-1.5">
                  <label className="text-xs font-semibold uppercase tracking-wider text-slate-400">Client</label>
                  <Select value={task.client_id || "none"} onValueChange={(v) => patch({ client_id: v === "none" ? null : v })}>
                    <SelectTrigger className="h-9" data-testid="task-client-select"><SelectValue placeholder="None" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">No client</SelectItem>
                      {clients.map((c) => <SelectItem key={c.id} value={c.id}>{c.company || c.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="space-y-1.5">
                <label className="text-xs font-semibold uppercase tracking-wider text-slate-400">Labels (comma separated)</label>
                <Input defaultValue={(task.labels || []).join(", ")} data-testid="task-labels-input"
                  onBlur={(e) => patch({ labels: e.target.value.split(",").map((s) => s.trim()).filter(Boolean) })} />
                {task.labels?.length > 0 && (
                  <div className="flex flex-wrap gap-1.5 mt-1.5">
                    {task.labels.map((l) => <span key={l} className={`text-[10px] font-medium px-1.5 py-0.5 rounded border ${labelColor(l)}`}>{l}</span>)}
                  </div>
                )}
              </div>

              {/* Move & duplicate */}
              <div className="flex items-end gap-2 flex-wrap bg-slate-50 border border-slate-200 rounded-lg p-3">
                <div className="flex-1 min-w-[180px] space-y-1.5">
                  <label className="text-xs font-semibold uppercase tracking-wider text-slate-400">Move to board</label>
                  <Select value={task.board_id} onValueChange={moveToBoard}>
                    <SelectTrigger className="h-9 bg-white" data-testid="task-move-board-select"><SelectValue /></SelectTrigger>
                    <SelectContent>{boards.map((b) => <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <Button variant="outline" className="h-9" onClick={duplicate} data-testid="task-duplicate-button">
                  <Copy className="w-4 h-4 mr-1.5" /> Duplicate
                </Button>
              </div>

              {/* Description */}
              <div className="space-y-1.5">
                <label className="text-xs font-semibold uppercase tracking-wider text-slate-400">Description</label>
                <Textarea defaultValue={task.description} placeholder="Add a description…" className="text-sm"
                  data-testid="task-description-input"
                  onBlur={(e) => e.target.value !== task.description && patch({ description: e.target.value })} />
              </div>

              {/* Subtasks */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <label className="text-xs font-semibold uppercase tracking-wider text-slate-400">
                    Subtasks ({task.subtasks.filter((s) => s.done).length}/{task.subtasks.length})
                  </label>
                </div>
                <div className="space-y-1.5">
                  {task.subtasks.map((s) => (
                    <div key={s.id} className="border border-slate-200 rounded-lg" data-testid={`task-subtask-item-${s.id}`}>
                      <div className="flex items-center flex-wrap gap-x-2.5 gap-y-1 px-3 py-2">
                        <Checkbox checked={s.done} onCheckedChange={() => toggleSub(s)} data-testid={`task-subtask-checkbox-${s.id}`} />
                        <span className={`flex-1 min-w-[120px] text-sm break-words ${s.done ? "line-through text-slate-400" : "text-slate-700"}`}>{s.title}</span>
                        <SubtaskAssignee sub={s} staff={staff} onChange={(v) => setSubAssignee(s, v)} />
                        <SubtaskDue sub={s} max={task.due_date ? task.due_date.slice(0, 10) : undefined} onChange={(v) => setSubDue(s, v)} />
                        <button onClick={() => toggleNotes(s.id)} aria-expanded={!!openNotes[s.id]} title="Notes & comments"
                          className={`h-7 px-1.5 rounded-md text-[11px] inline-flex items-center gap-1 shrink-0 transition-colors ${openNotes[s.id] ? "bg-indigo-50 text-indigo-600" : "text-slate-400 hover:bg-slate-100 hover:text-slate-600"}`}
                          data-testid={`subtask-notes-toggle-${s.id}`}>
                          <MessageSquare className="w-3.5 h-3.5" />{s.notes_count || 0}
                        </button>
                        <button onClick={() => deleteSub(s.id)} className="text-slate-300 hover:text-red-500 shrink-0" data-testid={`delete-subtask-${s.id}`}><Trash2 className="w-3.5 h-3.5" /></button>
                      </div>
                      {openNotes[s.id] && (
                        <div className="px-3 pb-3 border-t border-slate-100 bg-slate-50/50 rounded-b-lg" data-testid={`subtask-notes-panel-${s.id}`}>
                          <SubtaskNotes subtaskId={s.id} onAdded={() => { load(); onChanged?.(); }} />
                        </div>
                      )}
                    </div>
                  ))}
                </div>
                <div className="flex gap-2 mt-2">
                  <Input value={newSub} onChange={(e) => setNewSub(e.target.value)} onKeyDown={(e) => e.key === "Enter" && addSubtask()}
                    placeholder="Add a subtask…" className="h-9 text-sm" data-testid="task-add-subtask-input" />
                  <Button size="sm" className="h-9 bg-indigo-600 hover:bg-indigo-700" onClick={addSubtask} data-testid="task-add-subtask-button">
                    <Plus className="w-4 h-4" />
                  </Button>
                </div>
              </div>

              {/* Task notes */}
              <div>
                <label className="text-xs font-semibold uppercase tracking-wider text-slate-400 flex items-center gap-1.5 mb-2">
                  <MessageSquare className="w-3.5 h-3.5" /> Notes
                </label>
                <div className="flex gap-2">
                  <Textarea value={note} onChange={(e) => setNote(e.target.value)} placeholder="Share an update on this task…"
                    className="text-sm min-h-[64px]" data-testid="task-note-textarea" />
                </div>
                <Button size="sm" className="mt-2 bg-indigo-600 hover:bg-indigo-700" onClick={addNote} disabled={savingNote} data-testid="task-add-note-button">
                  {savingNote ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1.5" /> : <Send className="w-3.5 h-3.5 mr-1.5" />} Post note
                </Button>
                <p className="text-[11px] text-slate-400 mt-1">Tip: @mention a teammate (e.g. @priya) to flag them in the notes feed.</p>
                <NoteList notes={task.notes} />
              </div>

              <div className="pt-4 border-t border-slate-100">
                <Button variant="ghost" className="text-red-600 hover:text-red-700 hover:bg-red-50" onClick={deleteTask} data-testid="delete-task-button">
                  <Trash2 className="w-4 h-4 mr-1.5" /> Delete task
                </Button>
              </div>
            </div>
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
}

function StageOptions({ boardId }) {
  const [stages, setStages] = useState([]);
  useEffect(() => {
    api.get(`/boards/${boardId}`).then((r) => setStages(r.data.stages)).catch(() => {});
  }, [boardId]);
  return <>{stages.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}</>;
}
